import React, { useState, useEffect, useMemo } from "react";
import AddProductForm from "./AddProductForm";
import InventoryTransferForm from "./InventoryTransferForm";
import PrintLabelForm from "./PrintLabelForm";
import EditProductForm from "./EditProductForm";
import ImportProductForm from "./ImportProductForm";
import BulkAddProductForm from "./BulkAddProductForm";
import PendingCollectionsModal from "./WarehousePendingList";
import editIcon from "../assets/icons/EditIcon.svg";
import ItemTransferHistoryModal from "./ItemTransferHistoryModal";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;

// Adjust these to match the actual values stored in inventory_type if different
const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "PO", label: "PO" },
  { value: "FOC", label: "FOC" },
];

const ConditionDot = ({ condition }) => {
   const color = condition === 'faulty' ? '#e08a00' : '#2e9e44'; // orange : green
  return (
    <span
      title={condition === 'faulty' ? 'Faulty' : 'Working'}
      style={{
        display: 'inline-block',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: color,
        marginRight: '6px',
      }}
    />
  );
};

const InventorySearch = ({ refreshTrigger }) => {
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [showPrintLabelForm, setShowPrintLabelForm] = useState(false);
  const [refreshProducts, setRefreshProducts] = useState(false);
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const [showBulkAddProductForm, setShowBulkAddProductForm] = useState(false);
  const [showImportProductForm, setShowImportProductForm] = useState(false);
  const [showEditProductForm, setShowEditProductForm] = useState(false);
  const [showPendingCollections, setShowPendingCollections] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [locations, setLocations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [secureData, setSecureData] = useState(null);
  const [error, setError] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");       // raw input, updates every keystroke
  const [committedSearch, setCommittedSearch] = useState(""); // debounced value that actually drives the fetch
  const [showItemHistory, setShowItemHistory] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // --- Filter state ---
  // Free-text filter (debounced together with search)
  const [modelNameInput, setModelNameInput] = useState("");
  const [committedModelName, setCommittedModelName] = useState("");
  // Discrete filters (applied immediately, no debounce needed)
  const [typeFilter, setTypeFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-SG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  // Normalize the authenticated principal
  const currentUserId = useMemo(() => {
    const payload = secureData?.admin || secureData?.auth || null;
    return payload?.user_id ?? payload?.admin_id ?? null;
  }, [secureData]);

  // Refresh triggers from forms
  const handleProductAdded = () => {
    setRefreshProducts((x) => !x);
    setShowAddProductForm(false);
  };
  const handleTransferComplete = () => {
    setRefreshProducts((x) => !x);
    setShowTransferForm(false);
  };
  const handlePrintLabelComplete = () => {
    setRefreshProducts((x) => !x);
    setShowPrintLabelForm(false);
  };
  const handleImportComplete = () => {
    setRefreshProducts((x) => !x);
    setShowImportProductForm(false);
  };
  const handleDeviceReceived = () => {
    setRefreshProducts((x) => !x);
  };

  // Open Edit Form
  const handleEditProduct = (inventory) => {
    setSelectedInventory(inventory);
    setShowEditProductForm(true);
  };

  const handleSaveInventory = () => {
    fetchProducts();
    setShowEditProductForm(false);
  };

  // API fetchers
  const fetchProducts = async (
    pageToFetch = page,
    search = committedSearch,
    filters = {
      modelName: committedModelName,
      type: typeFilter,
      location: locationFilter,
      partner: partnerFilter,
      dateFrom,
      dateTo,
    }
  ) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(pageToFetch),
        limit: String(PAGE_SIZE),
      });
      if (search) params.set('search', search);
      if (filters.modelName) params.set('model_name', filters.modelName);
      if (filters.type) params.set('type', filters.type);
      if (filters.location) params.set('location', filters.location);
      if (filters.partner) params.set('partner', filters.partner);
      if (filters.dateFrom) params.set('date_from', filters.dateFrom);
      if (filters.dateTo) params.set('date_to', filters.dateTo);

      const response = await fetch(`/api/products?${params.toString()}`);
      if (!response.ok) throw new Error('Server responded with 500');
      const data = await response.json();

      setInventory(data.items || []);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.total || 0);
    } catch (err) {
      console.error("Error fetching products:", err);
      // Set an empty array so the map function doesn't crash the UI
      setInventory([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSecureData = () => {
    fetch("/api/secure-endpoint", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((r) => {
        if (r.status === 401 || r.status === 403) {
          throw new Error("Not authorized — please log in again.");
        }
        if (!r.ok) throw new Error("Failed to fetch secure data");
        return r.json();
      })
      .then(setSecureData)
      .catch((e) => {
        console.error("Error fetching secure data:", e);
        setError(e.message);
      });
  };

  const fetchLocations = () => {
    fetch("/api/locations")
      .then((r) => {
        if (!r.ok) throw new Error("Failed to fetch locations");
        return r.json();
      })
      .then(setLocations)
      .catch((e) => console.error("Error fetching locations:", e));
  };

  const fetchPartners = () => {
    fetch("/api/partners")
      .then((r) => {
        if (!r.ok) throw new Error("Failed to fetch partners");
        return r.json();
      })
      .then((data) => setPartners(Array.isArray(data) ? data : data.items || []))
      .catch((e) => console.error("Error fetching partners:", e));
  };

  useEffect(() => {
    fetchSecureData();
    fetchLocations();
    fetchPartners();
  }, [refreshTrigger]);

  // Debounce free-text inputs (search, model name) -> commit them, and jump back to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setCommittedSearch(searchQuery);
      setCommittedModelName(modelNameInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery, modelNameInput]);

  // Discrete filters (type, location, partner, date range) apply immediately -> jump back to page 1
  useEffect(() => {
    setPage(1);
  }, [typeFilter, locationFilter, partnerFilter, dateFrom, dateTo]);

  // The single source of truth for actually fetching a page of products
  useEffect(() => {
    fetchProducts(page, committedSearch, {
      modelName: committedModelName,
      type: typeFilter,
      location: locationFilter,
      partner: partnerFilter,
      dateFrom,
      dateTo,
    });
  }, [
    page,
    committedSearch,
    committedModelName,
    typeFilter,
    locationFilter,
    partnerFilter,
    dateFrom,
    dateTo,
    refreshTrigger,
    refreshProducts,
  ]);

  const handleClearFilters = () => {
    setSearchQuery("");
    setModelNameInput("");
    setTypeFilter("");
    setLocationFilter("");
    setPartnerFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (committedSearch) params.set('search', committedSearch);
    if (committedModelName) params.set('model_name', committedModelName);
    if (typeFilter) params.set('type', typeFilter);
    if (locationFilter) params.set('location', locationFilter);
    if (partnerFilter) params.set('partner', partnerFilter);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);

    fetch(`/api/export-inventory?${params.toString()}`)
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "inventory_report.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((e) => console.error("Error exporting inventory data:", e));
  };

  const goToPrevPage = () => setPage((p) => Math.max(1, p - 1));
  const goToNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="user-list-container">
      <h2>Product List</h2>

      {error && (
        <div className="error-banner">
          {error}{" "}
          <a href="/login" style={{ marginLeft: 8 }}>Login</a>
        </div>
      )}

      {/* Filters card */}
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          padding: '24px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Inventory Filters</h3>
            <p style={{ margin: '4px 0 0', color: '#666', fontSize: '14px' }}>
              Search and narrow inventory by type, location, and partner.
            </p>
          </div>
          <button type="button" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}>
          <label>Search Inventory</label>
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search Model Name, IMEI, Serial Number, Location..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsLoading(true);
              }}
              style={{ width: '100%' }}
            />
            {isLoading && <div className="search-spinner" aria-label="Searching" />}
          </div>
        </div>

        <div className="filter-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start' }}>
          <div className="filter-field" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label>Model Name</label>
            <input
              type="text"
              placeholder="e.g. S26 Ultra"
              value={modelNameInput}
              onChange={(e) => setModelNameInput(e.target.value)}
              style={{ width: '200px' }}
            />
          </div>

          <div className="filter-field" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label>Type</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: '130px' }}>
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-field" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label>Location</label>
            <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} style={{ width: '180px' }}>
              <option value="">All Locations</option>
              {locations.map((loc) => {
                const value = loc.location_id ?? loc.id;
                const label = loc.location_name ?? loc.name;
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="filter-field" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label>Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ width: '150px' }}
            />
          </div>

          <div className="filter-field" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label>Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ width: '150px' }}
            />
          </div>

          <div className="filter-field" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label>Partner</label>
            <select value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)} style={{ width: '200px' }}>
              <option value="">All Partners</option>
              {partners.map((p) => {
                const value = p.partner_id ?? p.id;
                const label = p.partner_name ?? p.name;
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <div style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>
          Showing <strong>{inventory.length}</strong> of <strong>{totalItems}</strong> inventory items
        </div>
      </div>

      <div className="button-container">
        <button onClick={() => setShowBulkAddProductForm(true)} disabled={!currentUserId}>Add Product</button>
        <button onClick={() => setShowTransferForm(true)} disabled={!currentUserId}>Transfer Inventory</button>
        <button onClick={() => setShowPrintLabelForm(true)} disabled={!currentUserId}>Print Label</button>
        <button onClick={() => setShowPendingCollections(true)} disabled={!currentUserId}>Warehouse</button>
        <button onClick={() => setShowItemHistory(true)} disabled={!currentUserId}>Item History</button>
        <button onClick={() => setShowImportProductForm(true)}>Import Products</button>
        <button onClick={handleExport}>Export Inventory</button>
      </div>

      {/* Add Product */}
      {showAddProductForm && currentUserId && (
        <AddProductForm        
          onClose={() => setShowAddProductForm(false)}
          onProductAdded={handleProductAdded}
          userId={currentUserId}
        />
      )}

      {/* Bulk Add Product */}
      {showBulkAddProductForm && currentUserId && (
        <BulkAddProductForm        
          onClose={() => setShowBulkAddProductForm(false)}
          onProductAdded={handleProductAdded}
          userId={currentUserId}
        />
      )}      

      {/* Transfer Inventory */}
      {showTransferForm && currentUserId && (
        <InventoryTransferForm
          onClose={() => setShowTransferForm(false)}
          inventory={inventory}
          locations={locations}
          fetchInventory={fetchProducts}
          onTransferComplete={handleTransferComplete}
          userId={currentUserId}
        />
      )}

      {/* Print Label */}
      {showPrintLabelForm && currentUserId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-button" onClick={() => setShowPrintLabelForm(false)}>&times;</button>
            <PrintLabelForm
              inventory={inventory}
              fetchInventory={fetchProducts}
              onPrintLabelComplete={handlePrintLabelComplete}
              userId={currentUserId}
            />
          </div>
        </div>
      )}

      {/* Pending Collections */}
      {showPendingCollections && currentUserId && (
        <PendingCollectionsModal
          onClose={() => setShowPendingCollections(false)}
          onDeviceReceived={handleDeviceReceived}
        />
      )}

      {/* Item History */}
      {showItemHistory && currentUserId && (
        <ItemTransferHistoryModal
          onClose={() => setShowItemHistory(false)}
        />
      )}

      {/* Import Products */}
      {showImportProductForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-button" onClick={() => setShowImportProductForm(false)}>&times;</button>
            <ImportProductForm onTransferComplete={handleImportComplete} />
          </div>
        </div>
      )}

      <div
        className="pagination-controls"
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          gap: '12px',
          alignItems: 'center',
          marginBottom: '12px',
        }}
      >
        <button onClick={goToPrevPage} disabled={page <= 1}style={{ backgroundColor: '#046d34', fontSize: '14px' }}>Previous</button>
        
        <button onClick={goToNextPage} disabled={page >= totalPages} style={{ backgroundColor: '#09096b', fontSize: '14px' }}>Next</button>
        <span style={{ color: '#666', fontSize: '14px' }}>
          Page {page} of {totalPages} ({totalItems} item{totalItems === 1 ? '' : 's'})
        </span>
      </div>

      <div className="UserList-container">
        <div className="inventory-table-wrapper">
          {isLoading && (
            <div className="inventory-loading-overlay">
              <div className="spinner" />
            </div>
          )}
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Colour</th>
                <th>Imei Number</th>
                <th>Serial Number</th>
                <th>Type</th>
                <th>Location</th>
                <th>DO No.</th>
                <th>Date</th>
                <th>Partner</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {inventory.length > 0 ? (
                inventory.map((p) => (
                  <tr key={p.inventory_id ?? p.serial_number}>
                    <td>  <ConditionDot condition={p.item_condition} />
  {p.asset1_name}</td>
                    <td>{p.asset2_name}</td>
                    <td>{p.imei_number && p.imei_number !== "null" ? p.imei_number : ""}</td>
                    <td>{p.serial_number}</td>
                    <td>{p.inventory_type}</td>
                    <td>{p.location_name}</td>
                    <td>{p.delivery_order_number}</td>
                    <td>{formatDate(p.latest_update_at)}</td>
                    <td>{p.partner_name}</td>
                    <td className="action-cell">
                      <div className="action-wrapper">
                        <button 
                          className="icon-action-btn" 
                          onClick={() => handleEditProduct(p)}
                          title="Edit Product"
                        >
                          <img src={editIcon} alt="Edit" className="table-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                    {isLoading ? 'Loading...' : 'No inventory items match your search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showEditProductForm && selectedInventory && (
        <EditProductForm
          inventory={selectedInventory}
          onClose={() => setShowEditProductForm(false)}
          onSave={handleSaveInventory}
        />
      )}
    </div>
  );
};

export default InventorySearch;